import { useState } from 'react';
import classNames from 'classnames';
import * as dayjs from 'dayjs';
import { t } from 'ttag';

import { Stars } from '@ui/Stars';
import { Button } from '@ui/Button';
import { Image } from '@ui/Image';
import { FullScreenCarousel } from '@ui/FullScreenCarousel';

import s from './ProductReview.sss';

interface Photo {
  idStr?: string | null,
  thumbnailUrl: string,
  originalUrl: string,
}

interface User {
  id: number,
  username: string,
  shopName?: string | null
}

export interface Review {
  id: number,
  overallRating: number,
  text?: string | null,
  createdAt: string,
  photos?: Photo[] | null,
  user: User,
}

interface Props {
  className?: string,
  review: Review,
}

export const ProductReview: React.FC<Props> = ({
  className,
  review,
}) => {
  const [photoIndex, setPhotoIndex] = useState(-1);
  const [isFullTextVisible, setIsFullTextVisible] = useState(false);

  const date = dayjs(review.createdAt).format('DD.MM.YYYY');
  const firstTextPart = review.text?.slice(0, 300);
  const secondTextPart = review.text?.slice(300);
  const photosCount = review.photos?.length || 0;
  const originalPhotos = review.photos?.map((photo) => photo.originalUrl) || [];
  const currentGalleyPhoto = originalPhotos[photoIndex];
  const nextGalleryPhoto = originalPhotos[(photoIndex + 1) % photosCount];
  const prevGalleryPhoto = originalPhotos[(photoIndex + photosCount - 1) % photosCount];
  const authorTitle = review.user.shopName || review.user.username;

  const handleNextGalleryPhoto = () => {
    setPhotoIndex(
      (prevIndex) => (prevIndex + 1) % photosCount,
    );
  };

  const handlePrevGalleryPhoto = () => {
    setPhotoIndex(
      (prevIndex) => (prevIndex + photosCount - 1) % photosCount,
    );
  };

  const handleCloseGallery = () => {
    setPhotoIndex(-1);
  };

  return (
    (
      <article className={classNames(s.root, className)}>
        <header className={s.header}>
          <p className={s.username}>
            {authorTitle}
          </p>
          <p className={s.date}>
            {date}
          </p>
        </header>
        <Stars
          className={s.rating}
          value={review.overallRating}
          readOnly={true}
        />
        {firstTextPart && (
        <p className={s.comment}>
          {firstTextPart}

          {secondTextPart && (
            isFullTextVisible ? (
              <span>
                {secondTextPart}
              </span>
            ) : (
              <>
                ...
                &nbsp;
                <Button
                  className={s.show}
                  theme="text"
                  onClick={() => setIsFullTextVisible(true)}
                >
                  {t`Ще`}
                </Button>
              </>
            )
          )}
        </p>
        )}
        {!!review.photos?.length && (
        <>
          <ul className={s.photoList}>
            {review.photos.map((photo, i) => (
              <li
                key={photo.idStr}
                className={s.photoItem}
              >
                <Button
                  className={s.photoButton}
                  theme="text"
                  onClick={() => setPhotoIndex(i)}
                >
                  <Image
                    className={s.photo}
                    src={photo.thumbnailUrl}
                    lazy={true}
                    width="72"
                    height="72"
                  />
                </Button>
              </li>
            ))}
          </ul>
          {currentGalleyPhoto && (
          <FullScreenCarousel
            mainSrc={currentGalleyPhoto}
            nextSrc={nextGalleryPhoto}
            prevSrc={prevGalleryPhoto}
            onCloseRequest={handleCloseGallery}
            onMoveNextRequest={handleNextGalleryPhoto}
            onMovePrevRequest={handlePrevGalleryPhoto}
          />
          )}
        </>
        )}
      </article>
    )
  );
};
